// Hook que devuelve los planes activos del kine — cross-pacientes.
// Es la versión cross-paciente de `usePlanesActivos` (que filtra por
// paciente específico, usado en DetallePaciente tab Rutinas).
//
// "Activo" = plan con `estado = 'activo'` perteneciente a un tratamiento
// de un paciente cuyo `kine_id` matchea el del kine logueado.
//
// FK chain del filtro:
//   planes.tratamiento_id → tratamientos.paciente_id → pacientes.kine_id
//
// Devuelve los planes ordenados por `fecha_inicio` DESC y los
// plan_ejercicios ordenados por `orden` ASC dentro de cada plan.
// El "naming UX" del producto trata estos planes como "rutinas"
// (ver docs/decisions/rutinas-prescription-flow.md).

import { useEffect, useState } from "react";
import { supabase } from "../client";

export interface PlanEjercicioActivoKine {
  id: string;
  ejercicio_id: string;
  ejercicio_nombre: string;
  orden: number;
  reps_objetivo: number;
  series_objetivo: number;
  frecuencia_semanal: number | null;
}

export interface PacientePlan {
  id: string;
  nombre: string;
  apellido: string;
}

export interface PlanActivoKine {
  id: string;
  nombre: string;
  fecha_inicio: string;
  fecha_fin: string | null;
  paciente: PacientePlan;
  ejercicios: PlanEjercicioActivoKine[];
}

interface State {
  data: PlanActivoKine[] | null;
  error: string | null;
  loading: boolean;
}

interface PlanRowRaw {
  id: string;
  nombre: string;
  fecha_inicio: string;
  fecha_fin: string | null;
  tratamientos: {
    paciente_id: string;
    pacientes: {
      id: string;
      kine_id: string;
      nombre: string;
      apellido: string;
    } | null;
  } | null;
  plan_ejercicios: Array<{
    id: string;
    ejercicio_id: string;
    orden: number;
    reps_objetivo: number;
    series_objetivo: number;
    frecuencia_semanal: number | null;
    ejercicios: { nombre: string } | null;
  }>;
}

export function usePlanesActivosKine(
  kineId: string | null | undefined,
): State {
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: !!kineId,
  });

  useEffect(() => {
    if (!kineId) {
      setState({ data: null, error: null, loading: false });
      return;
    }

    let cancelled = false;
    setState({ data: null, error: null, loading: true });

    (async () => {
      const { data, error } = await supabase
        .from("planes")
        .select(
          `
          id,
          nombre,
          fecha_inicio,
          fecha_fin,
          tratamientos!inner(
            paciente_id,
            pacientes!inner(id, kine_id, nombre, apellido)
          ),
          plan_ejercicios(
            id,
            ejercicio_id,
            orden,
            reps_objetivo,
            series_objetivo,
            frecuencia_semanal,
            ejercicios!inner(nombre)
          )
        `,
        )
        .eq("estado", "activo")
        .eq("tratamientos.pacientes.kine_id", kineId)
        .order("fecha_inicio", { ascending: false })
        .returns<PlanRowRaw[]>();

      if (cancelled) return;

      if (error) {
        console.warn("[usePlanesActivosKine] fetch error", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      const planes: PlanActivoKine[] = (data ?? [])
        // Defensiva: la JOIN puede devolver paciente null si la fila no
        // matchea (no debería con !inner, pero ts no lo garantiza).
        .filter((row) => row.tratamientos?.pacientes != null)
        .map((row) => ({
          id: row.id,
          nombre: row.nombre,
          fecha_inicio: row.fecha_inicio,
          fecha_fin: row.fecha_fin,
          paciente: {
            id: row.tratamientos!.pacientes!.id,
            nombre: row.tratamientos!.pacientes!.nombre,
            apellido: row.tratamientos!.pacientes!.apellido,
          },
          ejercicios: row.plan_ejercicios
            .map((pe) => ({
              id: pe.id,
              ejercicio_id: pe.ejercicio_id,
              ejercicio_nombre: pe.ejercicios?.nombre ?? pe.ejercicio_id,
              orden: pe.orden,
              reps_objetivo: pe.reps_objetivo,
              series_objetivo: pe.series_objetivo,
              frecuencia_semanal: pe.frecuencia_semanal,
            }))
            .sort((a, b) => a.orden - b.orden),
        }));

      setState({ data: planes, error: null, loading: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [kineId]);

  return state;
}
